<template>
  <LoadingWrapper :state="status">
    <div
      v-if="assessment"
      class="relative flex min-h-dvh w-full grow flex-col lg:h-dvh lg:min-h-0 lg:overflow-hidden"
    >
      <div
        class="bg-surface-container-low sticky top-0 z-30 flex h-20 shrink-0 items-center justify-between gap-4 px-2 py-4 md:px-6"
      >
        <div class="flex w-full items-center gap-2">
          <BackButton
            backup-route-name="lessons-detail"
            :params="{ lessonId }"
            :title="$t('common.back')"
            class="-ml-2"
          />
          <div class="flex flex-col gap-1">
            <h1 class="text-title-large">{{ $t('lumia.lesson.teacherAssessment.title') }}</h1>
            <div class="flex flex-row gap-1">
              <div
                class="bg-on-primary rounded-small text-label-medium flex flex-row items-center gap-1.5 py-1 pr-2 pl-1.5"
              >
                {{ assessment.title }}
              </div>
              <div
                v-for="team in assessment.teams"
                :key="team.id"
                class="bg-on-primary rounded-small text-label-medium flex flex-row items-center gap-1.5 py-1 pr-2 pl-1.5"
              >
                <BaseTeamAvatar :name="team.name" size="18" />
                <span>
                  {{ team.name }}
                </span>
              </div>
            </div>
          </div>
          <div class="ml-auto w-64 shrink-0">
            <div class="mb-1 flex flex-row items-center justify-between gap-2">
              <span class="text-body-medium text-on-surface-variant">Eingeschätzt</span>
              <span class="text-title-small"
                >{{ numberOfAssessedMembers }}/{{ assessment.members.length }}</span
              >
            </div>
            <BaseLinearProgressIndictor
              :percentage="(numberOfAssessedMembers / assessment.members.length) * 100"
            />
          </div>
        </div>
      </div>

      <div
        class="bg-surface-container-low grid grow grid-cols-1 overflow-hidden lg:min-h-0 lg:grid-cols-7"
      >
        <div
          class="sticky top-0 z-10 col-span-2 overflow-y-auto pb-4 lg:static lg:mr-2 lg:pt-0 lg:pb-4 lg:pl-4"
        >
          <BaseHorizontalOverflow>
            <ul class="flex w-max flex-row gap-1 px-4 lg:w-full lg:flex-col lg:gap-0.5 lg:px-0">
              <li
                v-for="entry in sortedMembers"
                :key="entry.member.id"
                class="lg:first:rounded-t-large lg:last:rounded-b-large overflow-hidden transition-all"
                :class="[
                  currentMember?.member.id === entry.member.id &&
                    'bg-secondary-container text-on-secondary-container rounded-large',
                  currentMember?.member.id != entry.member.id &&
                    'bg-surface-container-lowest rounded-small lg:rounded-extra-small',
                ]"
              >
                <FeedbackListItem
                  :member="teacherAssessmentMemberAsMember(entry.member)"
                  :total-questions="indikatorCount"
                  :total-answers="answeredCount(entry.member.id)"
                  :is-active="currentMember?.member.id === entry.member.id"
                  :is-finished="entry.teacherAssessment.given"
                  @click="selectMember(entry.member.id)"
                />
              </li>
            </ul>
          </BaseHorizontalOverflow>
          <p
            v-if="sortedMembers.length === 0"
            class="text-body-medium text-on-surface-variant px-2"
          >
            {{ $t('lumia.lesson.teacherAssessment.noMembers') }}
          </p>
        </div>
        <div
          ref="scrollContainer"
          class="bg-surface-container-lowest rounded-extra-large col-span-5 mx-4 mb-2 overflow-y-auto lg:mr-2 lg:ml-0"
        >
          <div v-if="currentMember" class="col-span-5 flex w-full flex-col gap-8 p-8">
            <div class="flex flex-row items-center gap-4">
              <BaseMemberAvatar
                :key="currentMember.member.id"
                :include-last-name="true"
                :member="currentMember.member"
                size="80"
              />
              <h2 class="text-headline-small md:text-display-small grow">
                {{ memberFullName(currentMember.member) }}
              </h2>
            </div>

            <p
              v-if="!currentMember.selfAssessment.given"
              class="text-body-medium text-on-surface-variant"
            >
              {{ $t('lumia.lesson.teacherAssessment.selfNotGiven') }}
            </p>

            <div
              v-if="assessment.kompetenzen.length === 0"
              class="text-body-medium text-on-surface-variant"
            >
              {{ $t('lumia.lesson.teacherAssessment.noIndikatoren') }}
            </div>

            <section
              v-for="kompetenz in assessment.kompetenzen"
              :key="kompetenz.id"
              class="flex flex-col gap-4"
            >
              <header class="flex flex-col gap-1">
                <h3 class="flex flex-row items-center gap-2">
                  <BaseChip :color="colorForUeberfachlicheKompetenz(kompetenz)">
                    {{ kompetenz.code }}
                  </BaseChip>
                  <span class="text-title-medium text-on-surface-variant">{{
                    kompetenz.title
                  }}</span>
                </h3>
              </header>
              <div class="flex flex-col gap-3">
                <TeacherAssessmentIndikator
                  v-for="indikator in indikatorenForKompetenz(kompetenz)"
                  :key="`${currentMember.member.id}-${indikator.id}`"
                  :indikator="indikator"
                  :teacher-scale="assessment.teacherScale!"
                  :self-scale="assessment.selfScale"
                  :member-firstname="currentMember.member.firstName"
                  :teacher-value="teacherValue(currentMember.member.id, indikator.id)"
                  :self-value="selfValue(currentMember, indikator.id)"
                  :frozen="assessment.endedAt != null"
                  @update:teacher-value="(value) => onTeacherValue(indikator.id, value)"
                />
              </div>
            </section>

            <div class="mt-8 flex w-full justify-between">
              <div>
                <BaseTextButton
                  v-if="prevMember"
                  color="outlined"
                  size="M"
                  icon="arrow_back"
                  @click="goToPrev"
                >
                  {{ memberFullName(prevMember.member) }}
                </BaseTextButton>
              </div>
              <div>
                <BaseTextButton
                  v-if="nextMember"
                  color="outlined"
                  size="M"
                  trailing-icon="arrow_forward"
                  @click="goToNext"
                >
                  {{ memberFullName(nextMember.member) }}
                </BaseTextButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </LoadingWrapper>
</template>

<script setup lang="ts">
  import { computed, ref, useTemplateRef, watch, watchEffect } from 'vue'
  import { useRouteQuery } from '@vueuse/router'
  import { useI18n } from 'vue-i18n'
  import BackButton from '@/core/components/BackButton.vue'
  import BaseChip from '@/core/components/base/chip/BaseChip.vue'
  import BaseHorizontalOverflow from '@/core/components/base/horizontal-overflow/BaseHorizontalOverflow.vue'
  import BaseMemberAvatar from '@/core/components/base/avatar/BaseMemberAvatar.vue'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import LoadingWrapper from '@/core/components/loading/LoadingWrapper.vue'
  import { compareMembersByName, memberFullName } from '@/core/models/member/member-name'
  import { useSnackStore } from '@/stores/snack.store'
  import FeedbackListItem from '@/modules/give-feedback/components/FeedbackListItem.vue'
  import TeacherAssessmentIndikator from '../components/TeacherAssessmentIndikator.vue'
  import { useTeacherAssessment } from '../composables/use-teacher-assessment'
  import { colorForUeberfachlicheKompetenz } from '../models/ueberfachliche-kompetenz-color'
  import {
    indikatorenForKompetenz,
    teacherAssessmentMemberAsMember,
    type MemberTeacherAssessment,
    type TeacherAssessment,
  } from '../models/teacher-assessment'
  import BaseTeamAvatar from '@/core/components/base/avatar/BaseTeamAvatar.vue'
  import BaseLinearProgressIndictor from '@/core/components/base/linear-progress-indicator/BaseLinearProgressIndictor.vue'

  const { t } = useI18n()
  const snackStore = useSnackStore()
  const { assessment, status, lessonId, saveAnswer } = useTeacherAssessment()

  const currentMemberId = useRouteQuery<string>('memberId', undefined, { mode: 'replace' })
  const scrollContainer = useTemplateRef<HTMLElement>('scrollContainer')

  /** memberId → indikatorId → scale value, or null for Not observed. Absent means unanswered. */
  const draftAnswers = ref<Record<string, Record<string, number | null>>>({})
  const savedAnswers = ref<Record<string, Record<string, number | null>>>({})

  watch(
    assessment,
    (value) => {
      const answers = answersFromAssessment(value)
      draftAnswers.value = answers
      savedAnswers.value = answersFromAssessment(value)
    },
    { immediate: true },
  )

  const indikatorCount = computed(
    () =>
      assessment.value?.kompetenzen.reduce(
        (sum, kompetenz) => sum + indikatorenForKompetenz(kompetenz).length,
        0,
      ) ?? 0,
  )

  const sortedMembers = computed(() =>
    [...(assessment.value?.members ?? [])].sort((a, b) => compareMembersByName(a.member, b.member)),
  )

  const currentMember = computed(() =>
    sortedMembers.value.find((entry) => entry.member.id === currentMemberId.value),
  )

  const currentIndex = computed(() =>
    sortedMembers.value.findIndex((entry) => entry.member.id === currentMemberId.value),
  )

  const prevMember = computed(() => {
    if (sortedMembers.value.length < 2) return undefined
    if (currentIndex.value > 0) return sortedMembers.value[currentIndex.value - 1]
    if (sortedMembers.value.length > 2) return sortedMembers.value[sortedMembers.value.length - 1]
    return undefined
  })

  const nextMember = computed(() => {
    if (sortedMembers.value.length < 2) return undefined
    if (currentIndex.value < sortedMembers.value.length - 1) {
      return sortedMembers.value[currentIndex.value + 1]
    }
    if (sortedMembers.value.length > 2) return sortedMembers.value[0]
    return undefined
  })

  watchEffect(() => {
    if (sortedMembers.value.length > 0 && !currentMemberId.value) {
      currentMemberId.value = sortedMembers.value[0].member.id
    }
  })

  const selectMember = (memberId: string) => {
    currentMemberId.value = memberId
    scrollTop()
  }

  const scrollTop = () => {
    scrollContainer.value?.scrollTo({ top: 0 })
  }

  const goToPrev = () => {
    if (prevMember.value) {
      currentMemberId.value = prevMember.value.member.id
      scrollTop()
    }
  }

  const goToNext = () => {
    if (nextMember.value) {
      currentMemberId.value = nextMember.value.member.id
      scrollTop()
    }
  }

  const teacherValue = (memberId: string, indikatorId: string): number | null | undefined => {
    const answers = draftAnswers.value[memberId]
    if (!answers || !(indikatorId in answers)) return undefined
    return answers[indikatorId]
  }

  const selfValue = (
    entry: MemberTeacherAssessment,
    indikatorId: string,
  ): number | null | undefined => {
    const answer = entry.selfAssessment.answers.find((item) => item.indikatorId === indikatorId)
    if (!answer) return undefined
    return answer.value
  }

  const onTeacherValue = async (indikatorId: string, value: number | null) => {
    const memberId = currentMember.value?.member.id
    if (!memberId || assessment.value?.endedAt) return
    if (teacherValue(memberId, indikatorId) === value) return

    setTeacherValue(memberId, indikatorId, value)
    const saved = await saveAnswer(memberId, indikatorId, value)
    if (saved) {
      rememberSavedAnswer(memberId, indikatorId, value)
      return
    }
    if (teacherValue(memberId, indikatorId) === value) {
      setTeacherValue(memberId, indikatorId, savedValue(memberId, indikatorId))
    }
    snackStore.showError(t('lumia.lesson.teacherAssessment.saveError'))
  }

  const setTeacherValue = (
    memberId: string,
    indikatorId: string,
    value: number | null | undefined,
  ) => {
    const next = { ...(draftAnswers.value[memberId] ?? {}) }
    if (value === undefined) {
      delete next[indikatorId]
    } else {
      next[indikatorId] = value
    }
    draftAnswers.value = { ...draftAnswers.value, [memberId]: next }
  }

  const answeredCount = (memberId: string) => Object.keys(draftAnswers.value[memberId] ?? {}).length

  const rememberSavedAnswer = (memberId: string, indikatorId: string, value: number | null) => {
    savedAnswers.value = {
      ...savedAnswers.value,
      [memberId]: { ...(savedAnswers.value[memberId] ?? {}), [indikatorId]: value },
    }
  }

  const savedValue = (memberId: string, indikatorId: string): number | null | undefined => {
    const answers = savedAnswers.value[memberId]
    if (!answers || !(indikatorId in answers)) return undefined
    return answers[indikatorId]
  }

  function answersFromAssessment(
    value: TeacherAssessment | undefined,
  ): Record<string, Record<string, number | null>> {
    if (!value) return {}
    return Object.fromEntries(
      value.members.map((entry) => [
        entry.member.id,
        Object.fromEntries(
          entry.teacherAssessment.answers.map((answer) => [answer.indikatorId, answer.value]),
        ),
      ]),
    )
  }

  const numberOfAssessedMembers = computed(
    () =>
      assessment.value?.members.filter(
        (entry) => answeredCount(entry.member.id) === indikatorCount.value,
      ).length ?? 0,
  )
</script>
